import React from 'react';
import { formatDistanceToNow, parseISO } from 'date-fns';
import type { EpicSummary } from '../../../../shared/types/epics';
import { StateIndicator } from '../shared/StateIndicator';
import { StoryProgressBar } from '../shared/StoryProgressBar';
import { ArchiveControl } from './ArchiveControl';

interface EpicTableProps {
  epics: EpicSummary[];
  isLoading: boolean;
  error: Error | null;
  /** Receives the epic's URL-safe ref (encodes PK|SK) for both epic models. */
  onRowClick?: (epicRef: string) => void;
  /** FORGE-159: when viewing archived epics, show the archive badge + Unarchive. */
  showArchived?: boolean;
}

/** FORGE-159: small badge for an archived / auto-hidden epic. */
const ArchiveBadge: React.FC<{ epic: EpicSummary }> = ({ epic }) => {
  const a = epic.archive;
  if (!a?.hidden) return null;
  if (a.archived) {
    return (
      <span
        title={a.archiveReason ? `Archived: ${a.archiveReason}` : 'Archived'}
        className="text-2xs font-medium rounded px-1.5 py-0.5 bg-amber-100 text-amber-800 whitespace-nowrap"
      >
        Archived
      </span>
    );
  }
  return (
    <span title="Auto-hidden (completed)" className="text-2xs font-medium rounded px-1.5 py-0.5 bg-slate-100 text-slate-600 whitespace-nowrap">
      Auto-hidden
    </span>
  );
};

/**
 * FORGE-302 — cheap "PR open" indicator for the list view. EpicSummary only
 * carries `prUrl` (already scanned for every epic's META row); the richer
 * PRWatchState/fix-round detail lives on a separate PRWATCH# row that would
 * mean an extra DDB query PER epic in this list's Scan — worth avoiding here,
 * so the full picture (state, round, pending feedback) is reserved for the
 * epic detail page's PRTrackingCard instead. This is just "there's an open PR."
 */
const PRIndicator: React.FC<{ epic: EpicSummary }> = ({ epic }) => {
  if (!epic.prUrl) return null;
  return (
    <a
      href={epic.prUrl}
      target="_blank"
      rel="noreferrer"
      title="Open pull request"
      onClick={(e) => e.stopPropagation()}
      className="text-2xs font-medium rounded px-1.5 py-0.5 bg-primary-50 text-primary-700 border border-primary-100 whitespace-nowrap hover:bg-primary-100"
    >
      PR
    </a>
  );
};

const TITLE_TRUNCATE = 80;

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + '…';
}

function formatRelative(iso: string): string {
  try {
    return formatDistanceToNow(parseISO(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}

const HEADER_CLS =
  'px-4 py-2.5 text-2xs uppercase tracking-wide text-slate-500 font-semibold';

const SkeletonRows: React.FC<{ rows?: number }> = ({ rows = 8 }) => (
  <tbody>
    {Array.from({ length: rows }).map((_, i) => (
      <tr key={i} aria-hidden="true">
        {Array.from({ length: 8 }).map((__, j) => (
          <td key={j} className="px-4 py-3">
            <div className="h-3 bg-slate-200 rounded animate-pulse" />
          </td>
        ))}
      </tr>
    ))}
  </tbody>
);

/** Renders the epic table including loading skeleton and error / empty states. */
export const EpicTable: React.FC<EpicTableProps> = ({ epics, isLoading, error, onRowClick, showArchived }) => {
  if (isLoading) {
    return (
      <div data-testid="epic-table-skeleton" aria-live="polite" aria-label="Loading epics">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              <th className={HEADER_CLS}>Epic #</th>
              <th className={HEADER_CLS}>Title</th>
              <th className={HEADER_CLS}>Repo</th>
              <th className={HEADER_CLS}>State</th>
              <th className={HEADER_CLS}>Progress</th>
              <th className={HEADER_CLS}>Approved by</th>
              <th className={HEADER_CLS}>Updated</th>
            </tr>
          </thead>
          <SkeletonRows />
        </table>
      </div>
    );
  }

  if (error) {
    return (
      <div role="alert" className="text-danger-700 bg-danger-50 px-5 py-4 text-sm">
        Failed to load epics. {error.message}
      </div>
    );
  }

  if (epics.length === 0) {
    return (
      <div className="text-slate-500 px-5 py-10 text-sm text-center">
        No epics found
      </div>
    );
  }

  return (
    <table className="w-full text-sm">
      <thead className="bg-slate-50 border-b border-slate-200">
        <tr className="text-left">
          <th className={HEADER_CLS}>Epic #</th>
          <th className={HEADER_CLS}>Title</th>
          <th className={HEADER_CLS}>Repo</th>
          <th className={HEADER_CLS}>State</th>
          <th className={HEADER_CLS}>Progress</th>
          <th className={HEADER_CLS}>Approved by</th>
          <th className={HEADER_CLS}>Updated</th>
          <th className={HEADER_CLS}></th>
        </tr>
      </thead>
      <tbody>
        {epics.map((epic) => (
          <tr
            key={epic.id}
            data-testid={`epic-table-row-${epic.id}`}
            aria-label={`Epic ${epic.number}: ${epic.title}`}
            tabIndex={0}
            role="row"
            className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60 cursor-pointer
                       focus-visible:bg-primary-50"
            onClick={() => onRowClick?.(epic.epicRef)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onRowClick?.(epic.epicRef);
              }
            }}
          >
            <td className="px-4 py-3 font-mono text-xs text-slate-700 tabular-nums">
              #{epic.number}
            </td>
            <td className="px-4 py-3 text-slate-900" title={epic.title}>
              <div className="flex items-center gap-2">
                {epic.kind === 'bugfix' && (
                  <span aria-label="Bug fix" title="Bug fix" className="text-base">
                    🔧
                  </span>
                )}
                <span>{truncate(epic.title, TITLE_TRUNCATE)}</span>
                <PRIndicator epic={epic} />
                {showArchived && <ArchiveBadge epic={epic} />}
              </div>
            </td>
            <td className="px-4 py-3 font-mono text-xs text-slate-600">{epic.repo}</td>
            <td className="px-4 py-3">
              <StateIndicator state={epic.state} />
            </td>
            <td className="px-4 py-3">
              <StoryProgressBar completed={epic.completedStoryCount} total={epic.storyCount} />
            </td>
            <td className="px-4 py-3 text-slate-700">{epic.github.approvedBy ?? '—'}</td>
            <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{formatRelative(epic.updatedAt)}</td>
            <td className="px-4 py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
              <ArchiveControl epic={epic} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};
